// src/modules/clinical-notes-v2/components/AiSuggestionPanel.jsx
//
// Panel complementario que aparece debajo de las secciones de texto
// del editor (HPI, Assessment, Treatment, FollowUp). Agrega las
// sugerencias IA generadas para CADA care gap y coding opportunity,
// agrupadas por fuente. NO se mergea en el field principal — el
// doctor escribe libre arriba y aquí ve qué SUGIERE la IA para que
// el care gap / coding op se cierre.
//
// Cada sugerencia tiene un botón "Append to field above" que copia
// SU texto al final del field principal correspondiente (opt-in,
// no se mergea automáticamente).

import React from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import AutoAwesomeIcon  from "@mui/icons-material/AutoAwesome";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon   from "@mui/icons-material/KeyboardArrowUp";
import NorthIcon            from "@mui/icons-material/North";
import GppGoodIcon          from "@mui/icons-material/GppGood";
import LocalOfferIcon       from "@mui/icons-material/LocalOffer";

/**
 * Toma las sugerencias acumuladas y devuelve los items relevantes para
 * una sección dada. Item shape: { kind, sourceId, sourceLabel, text }.
 */
function collectItemsForSection({
  section,
  gaps,
  ops,
  careGapSuggestions,
  codingOpSuggestions,
}) {
  const items = [];

  // --- Care gaps ---
  for (const gap of gaps) {
    const st = careGapSuggestions[gap.careGapId];
    if (!st || st.status !== "ready") continue;
    const sug = st.data?.suggestion || {};
    let text = "";
    if (section === "hpi")        text = sug.hpi || "";
    if (section === "assessment") text = sug.assessment || "";
    if (section === "treatment")  text = joinPlan(sug.plan);
    if (section === "followUp")   text = sug.followUp || "";
    if (text?.trim()) {
      items.push({
        kind: "gap",
        sourceId: gap.careGapId,
        sourceLabel: gap.measure || `Care gap ${gap.careGapId}`,
        sub: gap.icd10 || gap.qm || null,
        text,
        extra: section === "treatment" && Array.isArray(sug.documentationCues) && sug.documentationCues.length
          ? `Documentation cues:\n${sug.documentationCues.map((c) => `• ${c}`).join("\n")}`
          : null,
      });
    }
  }

  // --- Coding opportunities ---
  for (const op of ops) {
    const st = codingOpSuggestions[op.codingOpportunityId];
    if (!st || st.status !== "ready") continue;
    const sug = st.data?.suggestion || {};
    let text = "";
    let extra = null;
    if (section === "hpi")        text = sug.hpiAddition || "";
    if (section === "assessment") text = sug.assessment || "";
    if (section === "treatment") {
      const examsText = (sug.examsRecommended || [])
        .map((e) => `• ${e.name}${e.rationale ? ` — ${e.rationale}` : ""}`)
        .join("\n");
      const meatText = sug.meatDocumentation
        ? `M.E.A.T.\n` +
          `Monitor: ${sug.meatDocumentation.monitor || "—"}\n` +
          `Evaluate: ${sug.meatDocumentation.evaluate || "—"}\n` +
          `Assess: ${sug.meatDocumentation.assess || "—"}\n` +
          `Treat: ${sug.meatDocumentation.treat || "—"}`
        : "";
      text  = [examsText, joinPlan(sug.plan)].filter(Boolean).join("\n\n");
      extra = meatText || null;
    }
    if (section === "followUp") text = ""; // coding ops no tienen followUp explícito hoy
    if (text?.trim() || extra?.trim()) {
      items.push({
        kind: "op",
        sourceId: op.codingOpportunityId,
        sourceLabel: op.suspectedDescription || op.suspectedIcd10 || `Coding op ${op.codingOpportunityId}`,
        sub: op.suspectedIcd10 || op.hccCategory || null,
        text: text || "",
        extra: extra || (section === "assessment" && sug.icd10Specificity
          ? `ICD-10 specificity: ${sug.icd10Specificity}` : null),
      });
    }
  }

  return items;
}

function joinPlan(plan) {
  if (!Array.isArray(plan) || plan.length === 0) return "";
  return plan.map((p) => `• ${p}`).join("\n");
}

export default function AiSuggestionPanel({
  section,            // "hpi" | "assessment" | "treatment" | "followUp"
  sectionLabel,
  gaps,
  ops,
  careGapSuggestions,
  codingOpSuggestions,
  onAppendToField,    // (text) => void; appendea al field principal
  defaultExpanded = true,
}) {
  const items = collectItemsForSection({
    section, gaps, ops, careGapSuggestions, codingOpSuggestions,
  });
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  if (items.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1,
        borderRadius: 2,
        bgcolor: "#e8f4ff",
        borderColor: "#90caf9",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 1, cursor: "pointer" }}
        onClick={() => setExpanded((v) => !v)}
      >
        <AutoAwesomeIcon fontSize="small" sx={{ color: "#1565c0" }} />
        <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#0d47a1", flex: 1 }}>
          AI suggestions for {sectionLabel} ({items.length})
        </Typography>
        <IconButton size="small" sx={{ color: "#1565c0" }}>
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Stack>

      <Collapse in={expanded} timeout="auto">
        <Stack spacing={1.25} sx={{ p: 1.5, pt: 0 }}>
          {items.map((it, idx) => (
            <Paper
              key={`${it.kind}-${it.sourceId}-${idx}`}
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: "background.paper",
                borderLeft: `4px solid ${it.kind === "gap" ? "#1565c0" : "#7b1fa2"}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                {it.kind === "gap"
                  ? <GppGoodIcon fontSize="small" sx={{ color: "#1565c0" }} />
                  : <LocalOfferIcon fontSize="small" sx={{ color: "#7b1fa2" }} />}
                <Typography variant="caption" fontWeight={800} sx={{ color: it.kind === "gap" ? "#0d47a1" : "#4a148c" }}>
                  {it.kind === "gap" ? "CARE GAP" : "CODING OPP"}
                </Typography>
                <Chip size="small" variant="outlined" label={it.sourceLabel} sx={{ maxWidth: 320 }} />
                {it.sub && <Chip size="small" variant="outlined" label={it.sub} />}
              </Stack>

              {it.text && (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", color: "text.primary", mb: 0.75 }}
                >
                  {it.text}
                </Typography>
              )}

              {it.extra && (
                <Box
                  sx={{
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                    p: 1,
                    mb: 0.75,
                    fontFamily: "monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {it.extra}
                </Box>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<NorthIcon />}
                  onClick={() => onAppendToField([it.text, it.extra].filter(Boolean).join("\n\n"))}
                  sx={{ textTransform: "none" }}
                >
                  Append to field above
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigator.clipboard?.writeText([it.text, it.extra].filter(Boolean).join("\n\n"))}
                  sx={{ textTransform: "none" }}
                >
                  Copy
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
}
